import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Volume2, VolumeX, Music } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Background music player for public display screens.
 * Two ways to use:
 *   1. <DisplayMusic url="..." />                — pass explicit URL
 *   2. <DisplayMusicByToken token="..." />       — fetches the display's music_url via /displays/public/{token}
 */
/**
 * DisplayMusic mit Playlist-Support:
 * - url kann einzelne URL sein oder mehrere URLs mit `|` getrennt (z.B. "url1.mp3|url2.mp3")
 * - Bei Audio-Playlist: <audio onended> wechselt automatisch zum nächsten Track (looping)
 */
export default function DisplayMusic({ url, autoStart = false, hideToggle = false }) {
  // Immer muted starten, damit Browser Autoplay zulässt
  const [muted, setMuted] = useState(true);
  const [trackIndex, setTrackIndex] = useState(0);
  const iframeRef = useRef(null);
  const audioRef = useRef(null);

  // Playlist parsen: durch `|` getrennte URLs
  const urls = useMemo(() => {
    if (!url || typeof url !== "string") return [];
    return url.split("|").map((s) => s.trim()).filter(Boolean);
  }, [url]);

  const currentUrl = urls[trackIndex] || urls[0] || "";
  const info = parseMusicUrl(currentUrl);
  const isPlaylist = urls.length > 1;

  const advance = useCallback(() => {
    setTrackIndex((i) => (i + 1) % Math.max(1, urls.length));
  }, [urls.length]);

  const unmute = () => {
    if (info?.kind === "audio") {
      try {
        if (audioRef.current) {
          audioRef.current.muted = false;
          audioRef.current.volume = 0.5;
          audioRef.current.play?.().catch(() => {});
        }
      } catch { /* ignore */ }
      return;
    }
    try {
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "unMute", args: "" }), "*"
      );
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "setVolume", args: [55] }), "*"
      );
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!info) return;
    let cancelled = false;

    // First-Gesture-Fallback (immer aktiv, auch bei autoStart falls Browser blockt)
    const onFirstGesture = () => {
      if (cancelled) return;
      setMuted(false);
      unmute();
    };
    window.addEventListener("click", onFirstGesture, { once: true });
    window.addEventListener("keydown", onFirstGesture, { once: true });
    window.addEventListener("touchstart", onFirstGesture, { once: true });

    // YouTube postMessage-Listener: warten auf onReady bevor wir Kommandos senden.
    // Ohne diese Handshake gehen mute/unmute-Befehle ins Leere und die Musik bleibt stumm.
    let readyRetryTimer;
    let unmuteRetries = 0;
    const onYtMessage = (e) => {
      if (typeof e.data !== "string") return;
      try {
        const d = JSON.parse(e.data);
        // onReady oder erstes infoDelivery → Player ist bereit
        if (autoStart && info.kind === "youtube" && (d.event === "onReady" || d.event === "initialDelivery" || d.event === "infoDelivery")) {
          if (cancelled) return;
          unmute();
          // Nochmals nach 500ms unmuten (manche Browser brauchen zweiten Versuch)
          if (unmuteRetries < 3) {
            unmuteRetries++;
            readyRetryTimer = setTimeout(() => {
              if (!cancelled) unmute();
            }, 500);
          }
          setMuted(false);
        }
        // Auto-Advance in Playlist-Mode: YouTube-Player-State 0 = ENDED
        // Bei einzeln geladenen `|`-URLs (nicht list=…) wechseln wir manuell zum nächsten Track.
        if (isPlaylist && info.kind === "youtube" && d.event === "onStateChange" && (d.info === 0 || d.info === "0")) {
          if (!cancelled) advance();
        }
      } catch { /* ignore */ }
    };
    window.addEventListener("message", onYtMessage);

    // autoStart: nach kurzer Verzögerung Unmute-Versuch. Für Audio direkt, für YouTube via postMessage
    let t;
    if (autoStart) {
      t = setTimeout(() => {
        if (cancelled) return;
        if (info.kind === "audio") {
          const el = audioRef.current;
          if (!el) return;
          el.muted = false;
          el.volume = 0.5;
          const p = el.play?.();
          if (p && typeof p.then === "function") {
            p.then(() => { if (!cancelled) setMuted(false); })
             .catch(() => { if (!cancelled && el) el.muted = true; });
          } else {
            setMuted(false);
          }
        } else {
          // Handshake für YouTube-Player: „listening" registrieren
          try {
            iframeRef.current?.contentWindow?.postMessage(
              JSON.stringify({ event: "listening", id: "displaymusic", channel: "widget" }), "*"
            );
          } catch { /* ignore */ }
          // Erster Unmute-Versuch (falls Player schon läuft)
          unmute();
        }
      }, 400);
    }

    return () => {
      cancelled = true;
      if (t) clearTimeout(t);
      if (readyRetryTimer) clearTimeout(readyRetryTimer);
      window.removeEventListener("message", onYtMessage);
      window.removeEventListener("click", onFirstGesture);
      window.removeEventListener("keydown", onFirstGesture);
      window.removeEventListener("touchstart", onFirstGesture);
    };
  }, [info?.kind, info?.id, autoStart, isPlaylist, advance]);

  if (!info) return null;

  const mute = () => {
    if (info.kind === "audio") {
      if (audioRef.current) audioRef.current.muted = true;
      return;
    }
    try {
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "mute", args: "" }), "*"
      );
    } catch { /* ignore */ }
  };

  const toggle = () => {
    const next = !muted;
    setMuted(next);
    next ? mute() : unmute();
  };

  return (
    <>
      {info.kind === "audio" ? (
        <audio
          ref={audioRef}
          src={info.src}
          autoPlay
          loop={!isPlaylist}
          muted={muted}
          preload="auto"
          onEnded={isPlaylist ? advance : undefined}
          style={{ display: "none" }}
        />
      ) : info.kind === "spotify" ? (
        // Spotify-Embed unsichtbar (1×1 offscreen). Autoplay ist in src=?autoplay=1
        // gesetzt; falls Browser Autoplay blockt, startet der First-Gesture-Handler
        // (window click/keydown/touchstart) den Player durch Neuladen des iframes.
        <iframe
          ref={iframeRef}
          title="spotify-music"
          src={info.src}
          allow="autoplay; encrypted-media; clipboard-write; fullscreen; picture-in-picture"
          style={{ position: "fixed", width: 1, height: 1, opacity: 0, pointerEvents: "none", left: -10, bottom: -10 }}
          aria-hidden
        />
      ) : (
        <iframe
          ref={iframeRef}
          title="display-music"
          src={info.src}
          allow="autoplay; encrypted-media"
          style={{ position: "fixed", width: 1, height: 1, opacity: 0, pointerEvents: "none", left: -10, bottom: -10 }}
          aria-hidden
        />
      )}
      {hideToggle ? null : (
      <button
        data-testid="display-music-toggle"
        onClick={toggle}
        className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-[0.3em] backdrop-blur border transitions-safe ${
          muted
            ? "bg-black/50 border-white/15 text-[#F5F0E6]/80 hover:text-[#D4AF37] hover:border-[#D4AF37]/60"
            : "bg-[#D4AF37]/90 text-black border-[#D4AF37]"
        }`}
        title={muted ? "Musik einschalten" : "Musik stumm"}
      >
        {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
        <Music size={12} className="opacity-60" />
        <span className="hidden sm:inline">{muted ? "Musik an" : "Musik läuft"}</span>
      </button>
      )}
    </>
  );
}

function parseMusicUrl(url) {
  if (!url || typeof url !== "string") return null;
  const u = url.trim();
  if (!u) return null;
  // Direct audio file
  if (/\.(mp3|ogg|wav|m4a|aac)($|\?)/i.test(u)) {
    return { kind: "audio", src: u };
  }
  // Spotify (Playlist / Album / Track / Artist / Episode / Show) — Embed via open.spotify.com/embed
  let sp = u.match(/open\.spotify\.com\/(?:intl-\w+\/)?(playlist|album|track|artist|episode|show)\/([\w]+)/i);
  if (!sp) sp = u.match(/spotify:(?:playlist|album|track|artist|episode|show):([\w]+)/i);
  if (sp) {
    const kind = sp[1] || "playlist";
    const id = sp[2] || sp[1]; // uri-Fallback
    const src = `https://open.spotify.com/embed/${kind.toLowerCase()}/${id}?utm_source=generator&autoplay=1`;
    return { kind: "spotify", id, src };
  }
  // origin ist wichtig, damit YouTube-postMessage-Events (onReady/onStateChange) durchkommen —
  // ohne origin bleibt der Player still weil unsere Unmute-Kommandos ins Leere gehen.
  const origin = typeof window !== "undefined" ? encodeURIComponent(window.location.origin) : "";
  const common = `autoplay=1&mute=1&loop=1&controls=0&modestbranding=1&playsinline=1&enablejsapi=1&iv_load_policy=3&rel=0&origin=${origin}`;
  // YouTube playlist (music.youtube.com auch berücksichtigen)
  let m = u.match(/[?&]list=([\w-]+)/);
  if (m) {
    const listId = m[1];
    const src = `https://www.youtube-nocookie.com/embed/videoseries?list=${listId}&${common}`;
    return { kind: "youtube", id: listId, src };
  }
  // YouTube single video (youtube.com/watch?v=... , music.youtube.com/watch?v=... , youtu.be/...)
  let vid = "";
  m = u.match(/[?&]v=([\w-]+)/);
  if (m) vid = m[1];
  else {
    m = u.match(/youtu\.be\/([\w-]+)/);
    if (m) vid = m[1];
  }
  if (!vid) {
    m = u.match(/youtube\.com\/(?:embed|shorts)\/([\w-]+)/);
    if (m) vid = m[1];
  }
  if (vid) {
    const src = `https://www.youtube-nocookie.com/embed/${vid}?${common}&playlist=${vid}`;
    return { kind: "youtube", id: vid, src };
  }
  return null;
}

export function DisplayMusicByToken({ token, autoStart = false, hideToggle = false }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/displays/public/${token}`).then((r) => setUrl(r.data?.music_url || "")).catch(() => {});
  }, [token]);
  if (!url) return null;
  return <DisplayMusic url={url} autoStart={autoStart} hideToggle={hideToggle} />;
}
